/**
 * @file OrderShipmentCard.tsx
 * @description Карточка отгрузки на вкладке «Отгрузки» (phase19.12 шаги 5–6).
 *
 * Вынесена из OrderShipmentsTab: со строкой тарифа, местами и кнопкой расчёта
 * вкладка перестала помещаться в лимит 200 строк.
 *
 * Переданная отгрузка не показывает кнопку «Передать в СДЭК» — повторная
 * передача всё равно запрещена сервером. Вместо неё — статус заявки, трек и
 * печатные формы.
 *
 * phase19.13 шаг 6: сюда же приезжает история статусов перевозчика — лента
 * грузится лениво (`useShipmentEvents`), а её последнее событие питает бейдж
 * «зависла» и строку про неудачное вручение.
 */

import { useParams } from "react-router";

import { ShipmentCardActions } from "./ShipmentCardActions";
import { ShipmentCardSummary } from "./ShipmentCardSummary";
import { ShipmentEventsSection } from "./ShipmentEventsSection";
import { useShipmentEvents } from "./useShipmentEvents";
import { EditorialCard } from "../ui/EditorialCard";
import { useDeliveryThresholds } from "../../hooks/useDeliverySettings";
import type { ShipmentDto } from "../../lib/api-orders";
import type { ShipmentPrintForm } from "../../lib/api-carriers-sdek";
import { isShipmentHandedToCarrier, shipmentStaleness } from "../../lib/shipment-state";

interface Props {
  shipment: ShipmentDto;
  canManage: boolean;
  /**
   * По заказу возвращали деньги. Показываем предупреждение на черновиках,
   * но кнопку «Передать в СДЭК» не блокируем — отгрузить или нет решает
   * менеджер (решение пользователя 2026-08-14).
   */
  orderRefunded?: boolean;
  acting: boolean;
  /** Печатная форма, которая готовится у этой отгрузки. */
  printing?: ShipmentPrintForm | null;
  /** Цена доставки для клиента, если тариф отгрузки совпал с заказом. */
  clientPrice?: number | null;
  onQuote: () => void;
  onPickPoint: () => void;
  onSendToSdek: () => void;
  onSyncSdek: () => void;
  onPrint: (form: ShipmentPrintForm) => void;
  onShip: () => void;
  onDeliver: () => void;
  /** Удаление черновика; `onDone` вызывается после запроса — закрыть диалог. */
  onDelete: (onDone: () => void) => void;
  /** Ручной трек-номер (перевозчики без интеграции, phase19.15). */
  onSaveTrack: (trackNumber: string) => void;
}

export function OrderShipmentCard({
  shipment: s,
  canManage,
  orderRefunded = false,
  acting,
  printing,
  clientPrice,
  onQuote,
  onPickPoint,
  onSendToSdek,
  onSyncSdek,
  onPrint,
  onShip,
  onDeliver,
  onDelete,
  onSaveTrack,
}: Props) {
  const { slug } = useParams<{ slug: string }>();
  const handed = isShipmentHandedToCarrier(s);

  // История статусов: последнее событие тянем сразу (без него бейдж «зависла»
  // считался бы по времени создания отгрузки), полную ленту — при раскрытии.
  const events = useShipmentEvents(s.id, acting, handed);
  const thresholds = useDeliveryThresholds();
  // Пока зонд не ответил, бейдж не рисуем: считать «без движения» по времени
  // создания отгрузки, у которой на самом деле есть свежие события, — врать.
  const stale =
    handed && !events.probed ? null : shipmentStaleness(s, events.lastEvent, thresholds);
  const lastStage = events.lastEvent?.stage ?? null;
  // Плохой финал доставки: красная полоса слева и раскрытая лента.
  const failed =
    lastStage === "NOT_DELIVERED" || lastStage === "RETURNED" || s.status === "RETURNED";

  return (
    <EditorialCard className={failed ? "border-l-4 border-l-destructive" : undefined}>
      <EditorialCard.Body className="flex flex-wrap items-start justify-between gap-3">
        <ShipmentCardSummary
          shipment={s}
          handed={handed}
          orderRefunded={orderRefunded}
          clientPrice={clientPrice}
          stale={stale}
          lastStage={lastStage}
          lastComment={events.lastEvent?.comment ?? null}
        />

        {canManage ? (
          <ShipmentCardActions
            shipment={s}
            handed={handed}
            acting={acting}
            printing={printing}
            onQuote={onQuote}
            onPickPoint={onPickPoint}
            onSendToSdek={onSendToSdek}
            onSyncSdek={onSyncSdek}
            onPrint={onPrint}
            onShip={onShip}
            onDeliver={onDeliver}
            onDelete={onDelete}
            onSaveTrack={onSaveTrack}
          />
        ) : null}

        {/* История статусов перевозчика — на всю ширину под действиями. */}
        {handed ? (
          <ShipmentEventsSection
            events={events}
            autoOpen={failed || stale !== null}
            active={s.status !== "DELIVERED" && s.status !== "RETURNED"}
            carrierSettingsHref={
              s.carrier === "SDEK"
                ? `/shop/${slug ?? ""}/carriers/sdek/settings`
                : `/shop/${slug ?? ""}/carriers`
            }
            // Опрашивать статус умеет только СДЭК — у остальных отгрузок
            // кнопка была бы обманом.
            onSync={canManage && s.carrier === "SDEK" ? onSyncSdek : undefined}
            acting={acting}
          />
        ) : null}
      </EditorialCard.Body>
    </EditorialCard>
  );
}
